import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

/**
 * PM Tracker — Premier's PM workbook as a dashboard (Josh, 2026-10-19).
 *
 * Two windows on one page, Premier Precast and Kirchhoff Stone, each a grid
 * of jobs with the master-sheet headers and a milestone stoplight. See
 * pmTrackerSpec.ts for the rules and their sources.
 *
 * All four PM stores are loaded on entry. They are small — one row per job,
 * per task, per file — but checklist items run ~30-40 per job, so they are
 * paged rather than trusted to fit one request.
 */
const PAGE_SIZE = 5000;

const findAll = async (store: ServiceStore | undefined, query: Record<string, unknown>) => {
  if (!store?.find) return;
  let skip = 0;
  for (;;) {
    const res = (await store.find({
      query: { ...query, $limit: PAGE_SIZE, $skip: skip, $sort: { id: 1 } },
    })) as { data?: unknown[] } | unknown[] | null;
    if (!res) return;
    const data = Array.isArray(res) ? res : (res.data ?? []);
    skip += data.length;
    if (data.length < PAGE_SIZE || skip > 100000) return;
  }
};

const PmTrackerRoute: RouteType = {
  name: "PM Tracker",
  path: "/pm-tracker",
  title: "Project Management",
  layout: "/app",
  group: "Project Management",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./PmTracker.Page"),
      title: "Project Management",
      enableRefreshButton: false,
      enableBackButton: true,
    },
    body: {
      component: () => import("./PmTracker.Body"),
      grayBackground: true,
    },
  },
  channels: ["pm-jobs", "pm-checklist-items", "pm-documents", "pm-checklist-templates"],
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    const stores = rootStore as unknown as Record<string, ServiceStore | undefined>;
    try {
      await Promise.allSettled([
        findAll(stores.pmJobsStore, {}),
        findAll(stores.pmChecklistItemsStore, {}),
        findAll(stores.pmDocumentsStore, {}),
        findAll(stores.pmChecklistTemplatesStore, {}),
      ]);
    } catch (error) {
      console.warn("[PmTrackerRoute] warm-load failed:", error);
    }
    return true;
  },
};

export default PmTrackerRoute;
